/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useEffect, useMemo, useState } from "react";

import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  LinearScale
} from "chart.js";
import { createEmptyBarChart } from "@/Pages/Dashboard/config";
import { useFormattedNumber } from "@/shared";
import { Typography } from "@mui/material";

import { colors } from "@dotnet/telematics-design-system";

import {
  CREDITS_HEALTH,
  creditsObjectType,
  CreditsStatuses
} from "../../../types";

Chart.register(BarController, BarElement, CategoryScale, LinearScale);

interface CreditsHorizontalBarChartProps {
  creditsObjectArray: creditsObjectType[] | null;
  totalWithOverdraft: number;
  healthStatus: CREDITS_HEALTH;
}

const STATUS_COLOR_MAP: Record<string, string> = {
  [CreditsStatuses.Reserved]: colors.blue600,
  [CreditsStatuses.Available]: colors.blue500
};

const CreditsHorizontalBarChart = ({
  creditsObjectArray,
  totalWithOverdraft,
  healthStatus
}: CreditsHorizontalBarChartProps) => {
  const { t } = useTranslation();
  const { renderFormattedNumber } = useFormattedNumber();

  const [animatedArray, setAnimatedArray] = useState<creditsObjectType[]>([]);

  useEffect(() => {
    if (!creditsObjectArray) return;

    setAnimatedArray(creditsObjectArray);
  }, [creditsObjectArray]);

  const statusArray = animatedArray
    .filter(item => item.name in STATUS_COLOR_MAP)
    .map(item => item.count);

  const maxValue = Math.max(...statusArray.filter(v => v > 0), 0);
  const spacerValue = maxValue * 0.01; // tiny spacer

  const getBorderRadius = (value: number) =>
    value === 0
      ? 0
      : { topLeft: 4, bottomLeft: 4, topRight: 4, bottomRight: 4 };

  const baseDatasets = animatedArray
    .filter(item => item.name in STATUS_COLOR_MAP)
    .map((item, index) => ({
      index,
      label: item.name,
      value: item.count,
      backgroundColor: STATUS_COLOR_MAP[item.name] ?? colors.gray200,
      ...(item.name === CreditsStatuses.Inactive && {
        borderColor: colors.gray400,
        borderWidth: 1
      })
    }));

  const datasets: any[] = [];

  const isLimitReached =
    healthStatus === CREDITS_HEALTH.LimitReached ||
    healthStatus === CREDITS_HEALTH.OverdraftLimitReached;

  baseDatasets.forEach((item, idx) => {
    if (item.value > 0) {
      datasets.push({
        index: item.index,
        label: item.label,
        data: [item.value],
        backgroundColor: item.backgroundColor,
        hoverBackgroundColor: item.backgroundColor,
        barThickness: 16,
        borderSkipped: false,
        borderRadius: getBorderRadius(item.value),
        ...(item.borderColor && {
          borderColor: item.borderColor,
          borderWidth: item.borderWidth
        })
      });

      if (idx < baseDatasets.length - 1) {
        datasets.push({
          label: "",
          data: [spacerValue],
          backgroundColor: "transparent",
          barThickness: 0,
          isSpacer: true
        });
      }
    }
  });

  const totalStack = baseDatasets.reduce((sum, item) => sum + item.value, 0);
  const numSpacers = Math.max(datasets.length - 1, 0);
  const totalSpacers = spacerValue * numSpacers;

  const data = {
    labels: ["Credits"],
    datasets
  };

  const options: any = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        display: false,
        min: 0,
        max: totalStack + totalSpacers
      },
      y: { stacked: true, display: false }
    },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false }
    }
  };

  const emptyBarPlugin = useMemo(
    () => createEmptyBarChart(totalWithOverdraft, isLimitReached),
    [totalWithOverdraft]
  );

  return (
    <div
      css={css({
        width: "100%",
        margin: "16px 0",
        minHeight: "64px",
        display: "flex",
        flexDirection: "column",
        gap: "4px"
      })}
      data-testid={
        totalWithOverdraft === 0 || isLimitReached
          ? "empty-credits-canvas-holder"
          : "credits-canvas-holder"
      }
    >
      <div
        css={css({
          display: "flex",
          justifyContent: "space-between"
        })}
      >
        <Typography variant="body3Medium" sx={{ color: colors.textSecondary }}>
          0
        </Typography>

        <Typography variant="body3Medium" sx={{ color: colors.textSecondary }}>
          {renderFormattedNumber(totalWithOverdraft)}
        </Typography>
      </div>
      <div
        css={css({
          width: "100%",
          height: "16px",
          alignItems: "center",
          padding: "4px 0"
        })}
      >
        <Bar
          id="credits-horizontal-bar-chart"
          data={data}
          options={options}
          plugins={[emptyBarPlugin]}
        />
      </div>

      <div css={css({ display: "flex", gap: "16px" })}>
        {baseDatasets.map(item => (
          <div
            key={item.label}
            css={css({ display: "flex", alignItems: "center", gap: "4px" })}
          >
            <span
              css={css({
                width: "12px",
                height: "12px",
                backgroundColor: item.backgroundColor,
                display: "flex",
                borderRadius: "50%"
              })}
            />
            <Typography
              variant="body3Medium"
              sx={{ color: colors.textSecondary }}
            >
              {t(`Dashboard##credits statuses##${item.label}`)}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CreditsHorizontalBarChart;
